/**
 * Session persistence — localStorage read/write/clear.
 *
 * The pype-loader does NOT create sessions. Session creation is owned by
 * `<ps-pype>`/XState. The pype-loader merely persists session data to localStorage
 * when `<ps-pype>` dispatches `pype-session-created`, and clears it on
 * `pype-session-cleared` or `loader.shutdown()`/`loader.reset()`.
 *
 * @module
 */

const STORAGE_PREFIX = 'pype-session-';
const SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function getStorageKey(appId: string): string {
  return `${STORAGE_PREFIX}${appId}`;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

/** Shape of session data persisted in localStorage. */
export interface StoredSessionData {
  accessToken: string;
  chatId: string;
  userId: string;
  pypeId: string;
  streamId: string;
  /**
   * PII redaction patterns, persisted because a restored session never calls
   * `anonymous_session` again — without this, masking would be off after a
   * refresh. Optional so entries written before this field still validate.
   *
   * Opaque to the Loader: stored and handed back, never interpreted. Kept off
   * the documented `getState()` surface so it is not part of the embed API —
   * NOT a confidentiality guarantee, since the host page can read localStorage
   * and the `pype-session-created` event directly.
   */
  maskingRules?: string[];
  /** Client-side expiry timestamp (milliseconds). */
  expiresAt: number;
}

/**
 * Read a stored session from localStorage.
 *
 * Returns `null` if no session exists, the data is invalid, or it has expired.
 * Invalid/expired entries are cleaned up automatically.
 */
export function getStoredSession(appId: string): StoredSessionData | null {
  if (!isBrowser()) return null;

  const key = getStorageKey(appId);
  const raw = localStorage.getItem(key);
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !('accessToken' in parsed) ||
      !('chatId' in parsed) ||
      !('userId' in parsed) ||
      !('pypeId' in parsed) ||
      !('streamId' in parsed) ||
      !('expiresAt' in parsed)
    ) {
      localStorage.removeItem(key);
      return null;
    }
    const session = parsed as StoredSessionData;
    if (session.expiresAt < Date.now()) {
      localStorage.removeItem(key);
      return null;
    }
    return session;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

/** Persist session data to localStorage with a 7-day expiry. */
export function setStoredSession(
  appId: string,
  session: Omit<StoredSessionData, 'expiresAt'>
): void {
  if (!isBrowser()) return;

  const data: StoredSessionData = {
    ...session,
    expiresAt: Date.now() + SESSION_MAX_AGE_MS,
  };
  const key = getStorageKey(appId);
  localStorage.setItem(key, JSON.stringify(data));
}

/** Remove stored session from localStorage. */
export function clearStoredSession(appId: string): void {
  if (!isBrowser()) return;
  localStorage.removeItem(getStorageKey(appId));
}
